'use client';
import { createContext, useContext, useState, useEffect } from 'react';
import { I18N } from './i18n';
import { getAutoTranslation } from './i18n-auto';
// ⚡ PERF — COMMON (~3,4 Mo) n'est PLUS importé en statique : il alourdissait
// le bundle initial de TOUTES les pages alors que tx() renvoie le FR tel quel
// (donc inutile pour la majorité FR). Il est désormais chargé en LAZY dans
// LangProvider, et seulement si la langue active n'est pas le français.

// ═══ COMMON UI TRANSLATIONS — dans ./common-translations (serveur-importable), accessible via tx() ═══


// ═══════════════════════════════════════════════════════════════════════
// PROTECTION GLOBALE — AUCUNE variable i18n ne peut crasher l'app
// ═══════════════════════════════════════════════════════════════════════
// Problème: Certains fichiers utilisent tx(), tText(), td(), t(), lang, 
// f2(), fmt() au niveau MODULE (hors composant React).
// Or, useLang() ne fonctionne QUE dans un composant.
// Sans ce fallback → "tx is not defined" → page blanche → crash.
//
// Ce bloc garantit que TOUTES ces variables existent TOUJOURS,
// même avant que React ne monte les composants.
// Quand useLang() est appelé dans un composant, il override ces fallbacks.
// ═══════════════════════════════════════════════════════════════════════
if (typeof globalThis !== 'undefined') {
  // i18n — retourne le texte FR tel quel si pas encore initialisé
  if (!globalThis.tx)    globalThis.tx    = (s) => s;
  if (!globalThis.tText) globalThis.tText = (s) => s;
  if (!globalThis.td)    globalThis.td    = (key, _lang) => key;
  if (!globalThis.t)     globalThis.t     = (key) => key;
  
  // lang — français par défaut
  if (!globalThis.lang)  globalThis.lang  = 'fr';
  
  // Formatters — nombres belges
  if (!globalThis.f2)    globalThis.f2    = (n) => (Math.round((n||0)*100)/100).toFixed(2);
  if (!globalThis.fmt)   globalThis.fmt   = (n) => new Intl.NumberFormat('fr-BE',{minimumFractionDigits:2,maximumFractionDigits:2}).format(n||0);
  if (!globalThis.fmtE)  globalThis.fmtE  = (n) => new Intl.NumberFormat('fr-BE',{style:'currency',currency:'EUR'}).format(n||0);
}

export const LangContext = createContext({ lang: 'fr', setLang: () => {}, t: (k) => k, tx: (s) => s });

export function LangProvider({ children, initialLang = 'fr' }) {
  const [lang, setLangState] = useState(initialLang);
  // COMMON chargé à la demande (uniquement pour NL/EN/DE). null tant que non chargé.
  const [common, setCommon] = useState(null);

  useEffect(() => {
    const saved = typeof window !== 'undefined' ? (() => { try { return localStorage.getItem('aureus_lang'); } catch { return null; } })() : null;
    if (saved && ['fr','nl'].includes(saved)) setLangState(saved); // EN/DE retirés le 05/09/2026 : un ancien réglage en/de retombe sur FR
  }, []);

  // ⚡ Lazy-load des traductions communes (~3,4 Mo) SEULEMENT si la langue n'est
  // pas le FR. Tant que le chunk n'est pas arrivé, tx() retombe sur l'auto-trad
  // puis le FR (bref flash FR possible au tout 1er rendu d'un user non-FR).
  useEffect(() => {
    if (lang !== 'fr' && !common) {
      import('./common-translations').then(m => setCommon(m.COMMON)).catch(() => {});
    }
  }, [lang, common]);

  const setLang = (l) => {
    setLangState(l);
    try { if (typeof window !== 'undefined') localStorage.setItem('aureus_lang', l); } catch { /* handled */ }
  };

  const t = (key) => {
    const entry = I18N[key];
    if (!entry) return key;
    if (Array.isArray(entry[lang])) return entry[lang];
    return entry[lang] || entry['fr'] || key;
  };

  // tx = Traduire une string FR à la volée — pour les strings communes
  const tx = (frStr) => {
    if (lang === 'fr') return frStr;
    const entry = common && common[frStr];
    if (entry && entry[lang]) return entry[lang];
    const auto = getAutoTranslation(frStr, lang); // traductions auto-générées (cron i18n-translate)
    if (auto) return auto;
    return frStr; // Fallback FR si COMMON pas encore chargé ou clé absente
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t, tText: t, tx }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}

// Lecture de la langue courante HORS composant React (callbacks, générateurs
// de documents, etc.). Source de vérité : localStorage 'aureus_lang', avec
// repli sur globalThis.lang puis 'fr'. Server-safe (renvoie 'fr' côté serveur).
export function getCurrentLang() {
  if (typeof window !== 'undefined') {
    try {
      const s = localStorage.getItem('aureus_lang');
      if (['fr', 'nl'].includes(s)) return s;
    } catch { /* localStorage indisponible */ }
  }
  return (typeof globalThis !== 'undefined' && globalThis.lang) || 'fr';
}
